import { HttpClient, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { WINDOW } from 'src/app/core/core.module';
import {
    SelfApplyCandidates,
    SelfApplyFilter
} from 'src/app/workforce-disruption/shared/models/self-apply-candidate.model';
import { RecruitersInfo } from 'src/app/search-all-candidates/models/recruiters-info.model';
import { ContractStage } from 'src/app/shared/components/personal-professional-info/models/contract-stage.model';
import {
    LotusPayrollData,
    PayrollData
} from 'src/app/shared/components/personal-professional-info/models/payroll.model';
import {
    ProfileCategory,
    ProfileCategoryTagData,
    TagCategoryLookups
} from 'src/app/shared/components/personal-professional-info/models/profile-category.model';
import { AdditionalSkills } from 'src/app/shared/models/additional-skills.model';
import { QualificationsBackground } from 'src/app/shared/models/qualifications-background.model';
import { VaccinationInfo } from 'src/app/shared/models/vaccination-info.model';
import { appendQuery, combine } from 'src/app/shared/utilities/route';
import { JobInterest } from '../dashboard/models/job-interest.model';
import { CandidateData } from './details/models/candidate-data.model';
import { DashboardLead } from './details/models/dashboard-lead.model';
import { HotCandidate } from './details/models/hot-candidate.model';
import { HotItems } from './details/models/hot-items.model';
import { LabProviderEmailDetails } from './details/models/lab-provider-email-details.model';
import { LiveCandidateDetails } from './details/models/live-candidate-details.model';
import { LiveCandidateItems } from './details/models/live-candidate-item.model';
import { LiveCandidate } from './details/models/live-candidate.model';
import { PreStartCandidate, PreStartItems } from './details/models/prestart-candidate.model';
import { RecruiterJob } from './details/models/recruiter-job.model';
import { RecruiterLead } from './details/models/recruiter-lead.model';
import { WorkingCandidate, WorkingItems } from './details/models/working-candidate.model';
import { CandidateGridLookups } from './grid/models/candidate-grid-lookups';
import { AssignmentsGridItem } from './new-profile/models/assignments/assignments.model';
import {
    CandidateApplication,
    CandidateApplicationDoc,
    SkillsAssessment
} from './new-profile/models/candidate-application.model';
import {
    HotLiveStatus,
    HotLiveStatusEdit,
    HotLiveStatusRemove,
    HotLiveStatusRemoveLookupEntries
} from './new-profile/models/candidate-hotlive-status';
import { HotLiveRatingEnum, HotLiveStatusEnum } from './new-profile/models/candidate-hotlive-status.enum';
import { CandidatePerDiemStatus } from './new-profile/models/candidate-perdiem-status';
import { CandidateReassignInfo } from './new-profile/models/candidate-reassign-info.model';
import { CandidateReassign } from './new-profile/models/candidate-reassign.model';
import { CandidateSource } from './new-profile/models/candidate-source';
import {
    NotePinDto,
    NotesAndActivities,
    NotesAndActivitiesEdit,
    NotesAndActivitiesOrientationNote,
    ProfileAndOrientationNotes
} from './new-profile/models/notes-and-activities.model';
import { RecruiterAssignment } from './new-profile/models/recruiter-assignment.model';
import {
    CandidateReferenceChecks,
    CandidateReferenceChecksRequest,
    CandidateReferencePastActivity,
    CandidateReferenceStandardType,
    CandidateReferences,
    CandidateReferencesStatus
} from './new-profile/models/references/references.model';
import { ValidateContactInfoRequest } from './new-profile/models/validate-contact-info-request';
import { ValidateRTOComponentRequest } from './new-profile/models/validate-rto-component-request';
import { ProfileComponent } from './new-profile/store/profile-component.state';
import { TeamInfo } from './new-profile/team-info/models/team-info.model';
import { CandidatePayrollRep } from './new-profile/models/candidate-payroll-rep.model';
import { CandidateStatusResponse } from './new-profile/models/candidate-status-response';
import {
    CandidateReleaseReasonDto,
    CandidateReleaseReasonsDto
} from './new-profile/team-info/models/release-reason.model';

@Injectable({
    providedIn: 'root'
})
export class CandidateService {
    private coreApiPath: string;
    private readonly authPath: string;

    constructor(
        @Inject(APP_CONFIG) settings: Settings,
        private readonly http: HttpClient,
        @Inject(WINDOW) private readonly window: Window
    ) {
        this.coreApiPath = combine(settings.coreAPIUrl, 'ayanova');
        this.authPath = combine(settings.coreAPIUrl, 'auth-user');
    }

    getGridLookups(): Observable<CandidateGridLookups> {
        const url = `${this.coreApiPath}/Candidates/Lookups`;
        return this.http.get<CandidateGridLookups>(url);
    }

    getAllRecruiter(): Observable<RecruitersInfo[]> {
        const url = `${this.coreApiPath}/Candidates/Lookups/Recruiters`;
        return this.http.get<RecruitersInfo[]>(url);
    }

    getVerificationStatus(candidateId: number): Observable<boolean> {
        const url = `${this.coreApiPath}/Candidate/verification/${candidateId}/status`;

        return this.http.get<boolean>(url, { observe: 'response' }).pipe(
            map((resp) => {
                return resp.body;
            })
        );
    }

    addVerificationStatus(candidateId: number, isVerified: boolean): Observable<boolean> {
        const url = `${this.coreApiPath}/Candidate/verification`;
        const editDTO: object = { Id: 0, CandidateId: candidateId, IsVerified: isVerified };
        return this.http.post(url, editDTO).pipe(
            map((resp) => {
                return resp as boolean;
            })
        );
    }

    getEmailNoteTemplate(emailId: string): Observable<string> {
        const requestOptions: object = {
            responseType: 'text'
        };

        const url = `${this.coreApiPath}/candidate/${emailId}`;
        return this.http.get<string>(url, requestOptions);
    }

    getLabProviderEmailDetails(labProviderEmailId: number): Observable<LabProviderEmailDetails> {
        const url = `${this.coreApiPath}/candidate/lab-provider-email/${labProviderEmailId}`;
        return this.http.get<LabProviderEmailDetails>(url);
    }

    getRecruiterLeads(recruiterId: number, leadType: string): Observable<RecruiterLead[]> {
        const url = `${this.coreApiPath}/Candidate/Leads/${recruiterId}/${leadType}`;
        return this.http.get<RecruiterLead[]>(url);
    }

    getRecruiterLiveCandidates(recruiterId: number): Observable<LiveCandidate[]> {
        const url = `${this.coreApiPath}/candidates/live-nurses/leads/${recruiterId}`;
        return this.http.get<LiveCandidate[]>(url);
    }

    getRecruiterSelfApplyCandidates(recruiterId: number, filterData: SelfApplyFilter): Observable<SelfApplyCandidates> {
        const url = `${this.coreApiPath}/candidate/${recruiterId}/self-apply-items`;
        return this.http.post<SelfApplyCandidates>(url, filterData);
    }

    getLiveCandidateItems(recruiterId: number, maxItems: number): Observable<LiveCandidateItems> {
        const url = `${this.coreApiPath}/candidate/${recruiterId}/${maxItems}/live-items`;
        return this.http.get<LiveCandidateItems>(url);
    }

    getAllLiveCandidateItems(recruiterId: number): Observable<LiveCandidateItems> {
        const url = `${this.coreApiPath}/candidate/${recruiterId}/live-items`;
        return this.http.get<LiveCandidateItems>(url);
    }

    getLiveCandidateDetails(candidateId: number): Observable<LiveCandidateDetails> {
        const url = `${this.coreApiPath}/candidate/${candidateId}/live-item-details`;
        return this.http.get<LiveCandidateDetails>(url);
    }

    getAllLeadCandidateItems(recruiterId: number): Observable<DashboardLead[]> {
        const url = `${this.coreApiPath}/candidate/${recruiterId}/lead-items`;
        return this.http.get<DashboardLead[]>(url);
    }

    getPreStartItems(recruiterId: number, maxItems: number): Observable<PreStartItems> {
        const url = `${this.coreApiPath}/candidate/${recruiterId}/${maxItems}/prestart-items`;
        return this.http.get<PreStartItems>(url);
    }

    getAllPreStartItems(recruiterId: number): Observable<PreStartItems> {
        const url = `${this.coreApiPath}/candidate/${recruiterId}/all-prestart-items`;
        return this.http.get<PreStartItems>(url);
    }

    getPreStartDetails(candidateId: number): Observable<PreStartCandidate> {
        const url = `${this.coreApiPath}/candidate/${candidateId}/prestart-details`;
        return this.http.get<PreStartCandidate>(url);
    }

    getWorkingItems(recruiterId: number, maxItems: number): Observable<WorkingItems> {
        const url = `${this.coreApiPath}/candidate/${recruiterId}/${maxItems}/working-items`;
        return this.http.get<WorkingItems>(url);
    }

    getAllWorkingItems(recruiterId: number): Observable<WorkingItems> {
        const url = `${this.coreApiPath}/candidate/${recruiterId}/all-working-items`;
        return this.http.get<WorkingItems>(url);
    }

    getWorkingDetails(candidateId: number): Observable<WorkingCandidate> {
        const url = `${this.coreApiPath}/candidate/${candidateId}/working-details`;
        return this.http.get<WorkingCandidate>(url);
    }

    getRecruiterJobInterest(recruiterId: number): Observable<JobInterest[]> {
        const url = `${this.coreApiPath}/Candidate/${recruiterId}/job-interest`;
        return this.http.get<JobInterest[]>(url);
    }

    getRecruiterNewJobs(recruiterId: number, startDate: string): Observable<RecruiterJob[]> {
        const url = `${this.coreApiPath}/Candidate/Jobs/${recruiterId}/${startDate}`;
        return this.http.get<RecruiterJob[]>(url);
    }

    getCandidateSource(candidateId: number): Observable<CandidateSource> {
        const url = `${this.coreApiPath}/Candidate/${candidateId}/source`;
        return this.http.get<CandidateSource>(url);
    }

    getRecruiterPrestarts(recruiterId: number): Observable<PreStartCandidate[]> {
        const url = `${this.coreApiPath}/Candidate/prestarts/${recruiterId}`;
        return this.http.get<PreStartCandidate[]>(url);
    }

    getRecruiterWorkings(recruiterId: number): Observable<WorkingCandidate[]> {
        const url = `${this.coreApiPath}/Candidate/working/${recruiterId}`;
        return this.http.get<WorkingCandidate[]>(url);
    }

    getRecruiterHots(recruiterId: number): Observable<HotCandidate[]> {
        const url = `${this.coreApiPath}/Candidate/hots/${recruiterId}`;
        return this.http.get<HotCandidate[]>(url);
    }

    getRecruiterHotItems(recruiterId: number, maxCount: number, pendingOnly: boolean): Observable<HotItems> {
        const url = `${this.coreApiPath}/Candidate/${recruiterId}/${maxCount}/${pendingOnly}/hot-items`;
        return this.http.get<HotItems>(url);
    }

    getRecruiterHotDetails(candidateId: number): Observable<HotCandidate> {
        const url = `${this.coreApiPath}/Candidate/${candidateId}/hot-details`;
        return this.http.get<HotCandidate>(url);
    }

    getAdditionalSkills(candidateId: number): Observable<AdditionalSkills> {
        const url = `${this.coreApiPath}/Candidate/skills/${candidateId}`;

        return this.http.get<AdditionalSkills>(url);
    }

    updateAdditionalSkills(skills: AdditionalSkills): Observable<AdditionalSkills> {
        const url = `${this.coreApiPath}/Candidate/skills`;
        return this.http.post<AdditionalSkills>(url, skills);
    }

    getVaccinationInfo(candidateId: number): Observable<VaccinationInfo> {
        const url = `${this.coreApiPath}/Candidate/vaccinations/${candidateId}`;
        return this.http.get<VaccinationInfo>(url);
    }

    updateVaccinationInfo(vaccinationInfo: VaccinationInfo): Observable<VaccinationInfo> {
        const url = `${this.coreApiPath}/Candidate/vaccinations`;
        return this.http.post<VaccinationInfo>(url, vaccinationInfo);
    }

    getQualificationsBackground(candidateId: number): Observable<QualificationsBackground> {
        const url = `${this.coreApiPath}/candidate/${candidateId}/background`;
        return this.http.get<QualificationsBackground>(url);
    }

    updateQualificationsBackground(background: QualificationsBackground): Observable<QualificationsBackground> {
        const url = `${this.coreApiPath}/candidate/${background.candidateId}/background`;
        return this.http.post<QualificationsBackground>(url, background);
    }

    unblockUser(userId: string): Observable<any> {
        const url = `${this.authPath}/${userId}/unblock`;
        return this.http.post<any>(url, null);
    }

    getApplication(candidateId: number): Observable<CandidateApplication> {
        const url = `${this.coreApiPath}/Candidate/${candidateId}/application`;
        return this.http.get<CandidateApplication>(url);
    }

    downloadApplicationDoc(candidateId: number, applicationData: CandidateApplicationDoc, fileName: string): void {
        const url = `${this.coreApiPath}/Candidate/${candidateId}/download-application`;
        const doc = this.getFile(url, applicationData);
        this.downloadFile(doc, fileName);
    }

    getApplicationFile(
        candidateId: number,
        applicationData: CandidateApplicationDoc
    ): Observable<{ file: Blob; url: string }> {
        const url = `${this.coreApiPath}/Candidate/${candidateId}/download-application-docx`;
        return this.getFile(url, applicationData);
    }

    downloadSelectedAssessmentsDoc(candidateId: number, skillsAssessments: SkillsAssessment[], fileName: string): void {
        const url = `${this.coreApiPath}/Candidate/${candidateId}/download-skills-assessments`;
        const doc = this.getFile(url, skillsAssessments);
        this.downloadFile(doc, fileName);
    }

    downloadSelectedReferencesDoc(candidateId: number, employmentReferenceIds: number[], fileName: string): void {
        let url = `${this.coreApiPath}/Candidate/${candidateId}/download-employment-references?`;

        const queryParams: string = employmentReferenceIds
            .reduce((queryParams, currentId, index) => `${queryParams}referenceIds[${index}]=${currentId}&`, '')
            .slice(0, -1);

        url = `${url}${queryParams}`;
        const doc = this.getFile(url, queryParams, true);
        this.downloadFile(doc, fileName);
    }

    private getFile(url: string, applicationData: any, getRequest?: boolean): Observable<{ file: Blob; url: string }> {
        if (getRequest) {
            return this.http.get(url, { responseType: 'blob' }).pipe(
                map((file) => {
                    const url = this.window.window.URL.createObjectURL(file);
                    return { file, url };
                })
            );
        } else {
            return this.http.post(url, applicationData, { responseType: 'blob' }).pipe(
                map((file) => {
                    const url = this.window.window.URL.createObjectURL(file);
                    return { file, url };
                })
            );
        }
    }

    private downloadFile(value: Observable<{ file: Blob; url: string }>, fileName: string): void {
        value.subscribe((file) => {
            const downloadLink = this.window.document.createElement('a');
            downloadLink.download = fileName;
            downloadLink.href = file.url;
            downloadLink.click();
        });
    }

    getHotLiveStatus(candidateId: number): Observable<HotLiveStatus> {
        const url = `${this.coreApiPath}/Candidate/${candidateId}/hotlive-status`;
        return this.http.get<HotLiveStatus>(url);
    }

    getPerDiemStatus(candidateId: number): Observable<CandidatePerDiemStatus> {
        const url = `${this.coreApiPath}/perdiem-recruiting/candidates/${candidateId}/status`;
        return this.http.get<CandidatePerDiemStatus>(url);
    }

    addToLiveList(candidateId: number, rating: HotLiveRatingEnum, recruiterId: number): Observable<any> {
        const data: HotLiveStatusEdit = {
            candidateId: candidateId,
            status: HotLiveStatusEnum.Live,
            rating: rating,
            recruiterId: recruiterId
        };
        const url = `${this.coreApiPath}/candidate/${candidateId}/hotlive-status`;
        return this.http.put(url, data);
    }

    removeFromLiveList(candidateId: number, data: HotLiveStatusRemove): Observable<any> {
        const url = `${this.coreApiPath}/candidate/${candidateId}/live-list`;
        return this.http.post(url, data);
    }

    getAgenciesForHotLiveRemove(): Observable<HotLiveStatusRemoveLookupEntries[]> {
        const url = `${this.coreApiPath}/candidate/live-list/lookups/agencies`;
        return this.http.post<HotLiveStatusRemoveLookupEntries[]>(url, {});
    }

    getFacilitiesForHotLiveRemove(contains?: string): Observable<HotLiveStatusRemoveLookupEntries[]> {
        const url = `${this.coreApiPath}/facility/lookups/facility/filter`;
        const data = { keyword: contains, take: 100 };
        return this.http.post<HotLiveStatusRemoveLookupEntries[]>(url, data);
    }

    getCandidateAssignmentsCount(candidateId: number): Observable<number> {
        const url = `${this.coreApiPath}/candidate/${candidateId}/assignments/count`;
        return this.http.get<number>(url);
    }

    getCandidateAssignments(candidateId: number, skip: number, pageSize: number): Observable<AssignmentsGridItem> {
        let url = `${this.coreApiPath}/candidate/${candidateId}/assignments`;
        url = appendQuery(url, `skip=${skip}`, `pageSize=${pageSize}`);
        return this.http.get<AssignmentsGridItem>(url);
    }

    getCandidateActiveContracts(candidateId: number): Observable<ContractStage[]> {
        return this.http.get<ContractStage[]>(`${this.coreApiPath}/candidate/${candidateId}/active-contracts`);
    }

    getCandidateNotesAndActivities(candidateId: number, limitHistory: boolean): Observable<ProfileAndOrientationNotes> {
        return this.http.get<ProfileAndOrientationNotes>(
            `${this.coreApiPath}/candidate/${candidateId}/notes-and-activities?limitHistory=${limitHistory}`
        );
    }

    getRecruiterAssignment(candidateId: number): Observable<RecruiterAssignment> {
        return this.http.get<RecruiterAssignment>(`${this.coreApiPath}/candidate/${candidateId}/recruiter-assignment`);
    }

    pinANote(candidateId: number, notePin: NotePinDto): Observable<NotePinDto> {
        const url = `${this.coreApiPath}/candidate/${candidateId}/note/pin`;
        return this.http.post<NotePinDto>(url, notePin);
    }

    unpinANote(candidateId: number, notePinId: number): Observable<boolean> {
        const url = `${this.coreApiPath}/candidate/${candidateId}/note/unpin`;
        const params = { notePinId: notePinId.toString() };
        return this.http.delete<boolean>(url, { params });
    }

    addCandidateNotesAndActivities(newNote: NotesAndActivitiesEdit): Observable<NotesAndActivities> {
        const url = `${this.coreApiPath}/candidate/${newNote.userId}/notes-and-activities`;
        return this.http.post<NotesAndActivities>(url, newNote);
    }

    addCandidateSectionNote(newNote: NotesAndActivitiesEdit): Observable<NotesAndActivitiesOrientationNote> {
        const url = `${this.coreApiPath}/candidate/${newNote.userId}/orientation-note`;
        return this.http.post<NotesAndActivitiesOrientationNote>(url, newNote);
    }

    getCandidateTeam(candidateId: number): Observable<TeamInfo> {
        return this.http.get<TeamInfo>(`${this.coreApiPath}/candidate/${candidateId}/team-info`);
    }

    releaseCandidate(
        candidateId: number,
        recruiterId: number,
        isPerm: boolean = false,
        isWfd: boolean = false
    ): Observable<boolean> {
        if (isPerm) {
            return this.http.put<boolean>(
                `${this.coreApiPath}/candidate/${candidateId}/${recruiterId}/release/perm`,
                null
            );
        }
        if (isWfd) {
            return this.http.put<boolean>(
                `${this.coreApiPath}/candidate/${candidateId}/${recruiterId}/release/wfd`,
                null
            );
        }

        return this.http.put<boolean>(`${this.coreApiPath}/candidate/${candidateId}/${recruiterId}/release`, null);
    }

    assignCandidate(data: CandidateReassign): Observable<CandidateReassignInfo> {
        return this.http.post<CandidateReassignInfo>(`${this.coreApiPath}/candidate/assign`, data);
    }

    reassignCandidate(data: CandidateReassign): Observable<CandidateReassignInfo> {
        return this.http.post<CandidateReassignInfo>(`${this.coreApiPath}/candidate/reassign`, data);
    }

    getCandidateReferencesStatus(candidateId: number): Observable<CandidateReferencesStatus> {
        return this.http.get<CandidateReferencesStatus>(
            `${this.coreApiPath}/candidate/${candidateId}/references/status`
        );
    }

    getCandidateReferences(candidateId: number): Observable<CandidateReferences[]> {
        return this.http.get<CandidateReferences[]>(`${this.coreApiPath}/candidate/${candidateId}/references`);
    }

    saveCandidateReferences(candidateId: number, references: CandidateReferences[]): Observable<boolean> {
        return this.http.post<boolean>(`${this.coreApiPath}/candidate/${candidateId}/references`, references);
    }

    getProfileCategory(candidateId: number): Observable<ProfileCategory> {
        return this.http.get<ProfileCategory>(`${this.coreApiPath}/candidate/${candidateId}/profile-category`);
    }

    getProfileCategoryTags(candidateId: number): Observable<ProfileCategoryTagData> {
        return this.http.get<ProfileCategoryTagData>(
            `${this.coreApiPath}/candidate/${candidateId}/profile-category-tags`
        );
    }

    getProfileTagCategories(): Observable<TagCategoryLookups> {
        return this.http.get<TagCategoryLookups>(`${this.coreApiPath}/candidate/profile-category-tag-types`);
    }

    upsertProfileCategory(profileCategory: ProfileCategory): Observable<ProfileCategory> {
        return this.http.post<ProfileCategory>(`${this.coreApiPath}/candidate/profile-category`, profileCategory);
    }

    upsertProfileCategoryTags(profileCategoryTagData: ProfileCategoryTagData): Observable<ProfileCategoryTagData> {
        return this.http.post<ProfileCategoryTagData>(
            `${this.coreApiPath}/candidate/profile-category-tags`,
            profileCategoryTagData
        );
    }

    getCandidateData(candidateId: number): Observable<CandidateData> {
        return this.http.get<CandidateData>(`${this.coreApiPath}/candidate/${candidateId}/candidate-data`);
    }

    getCandidateReferenceChecks(candidateId: number): Observable<CandidateReferenceChecks[]> {
        return this.http.get<CandidateReferenceChecks[]>(
            `${this.coreApiPath}/candidate/${candidateId}/reference-checks`
        );
    }

    updateCandidateReferenceChecksStatus(
        candidateId: number,
        referenceCheckIds: number[],
        statusId: number
    ): Observable<void> {
        return this.http.put<void>(
            `${this.coreApiPath}/candidate/${candidateId}/reference-checks/status/${statusId}`,
            referenceCheckIds
        );
    }

    getCandidateReferenceStandardTypes(): Observable<CandidateReferenceStandardType[]> {
        return this.http.get<CandidateReferenceStandardType[]>(
            `${this.coreApiPath}/candidate/references/reference-standard-types`
        );
    }

    requestCandidateReferenceChecks(candidateId: number, data: CandidateReferenceChecksRequest): Observable<boolean> {
        return this.http.post<boolean>(`${this.coreApiPath}/candidate/${candidateId}/references/request-checks`, data);
    }

    getCandidatePastReferences(candidateId: number): Observable<CandidateReferencePastActivity> {
        return this.http.get<CandidateReferencePastActivity>(
            `${this.coreApiPath}/candidate/${candidateId}/references/past/activities`
        );
    }

    updatePayrollData(candidateId: number, payrollData: PayrollData): Observable<HttpResponse<any>> {
        return this.http.post<any>(`${this.coreApiPath}/candidate/${candidateId}/payroll-data`, payrollData, {
            observe: 'response'
        });
    }

    updateLotusPayrollData(candidateId: number, lotusPayrollData: LotusPayrollData): Observable<HttpResponse<any>> {
        return this.http.post<any>(`${this.coreApiPath}/candidate/${candidateId}/lotus-data`, lotusPayrollData, {
            observe: 'response'
        });
    }

    getCandidateProfileComponents(candidateId: number): Observable<ProfileComponent[]> {
        return this.http.get<ProfileComponent[]>(`${this.coreApiPath}/candidateprofilecomponents/${candidateId}`);
    }

    getShouldDisplayCandidateProfileComponents(candidateId: number): Observable<boolean> {
        return this.http.get<boolean>(
            `${this.coreApiPath}/candidateprofilecomponents/displayProfileAlerts/${candidateId}`
        );
    }

    verifyContactInfoComponents(
        validateContactInfoRequest: ValidateContactInfoRequest
    ): Observable<ProfileComponent[]> {
        return this.http.post<ProfileComponent[]>(
            `${this.coreApiPath}/candidateprofilecomponents/Validate/ContactInfo`,
            validateContactInfoRequest
        );
    }

    verifyRtoComponent(validateRTOComponentRequest: ValidateRTOComponentRequest): Observable<ProfileComponent[]> {
        return this.http.post<ProfileComponent[]>(
            `${this.coreApiPath}/candidateprofilecomponents/Validate/Rto`,
            validateRTOComponentRequest
        );
    }

    getDeviceTokens(candidateId: number): Observable<string[]> {
        return this.http.get<string[]>(`${this.coreApiPath}/candidate/${candidateId}/device-tokens`);
    }

    resetDeviceToken(candidateId: number): Observable<any> {
        return this.http.post<any>(`${this.coreApiPath}/candidate/${candidateId}/device-registration`, null);
    }

    npiIsValid(npi: number): Observable<boolean> {
        return this.http.get<boolean>(`${this.coreApiPath}/candidate/npi-validation/${npi}`);
    }

    getCandidateActiveContractStageCodes(candidateId: number): Observable<string[]> {
        return this.http.get<string[]>(`${this.coreApiPath}/candidate/${candidateId}/active-contract-stage-codes`);
    }

    getPayrollReps(): Observable<CandidatePayrollRep[]> {
        return this.http.get<CandidatePayrollRep[]>(
            `${this.coreApiPath}/facility-contracts/facility-systems/payroll-liaisons`
        );
    }

    savePayrollRep(candidateId: number, payrollRepId: number): Observable<void> {
        const url = `${this.coreApiPath}/candidate/${candidateId}/assign-payroll-rep`;
        return this.http.post<void>(url, payrollRepId);
    }

    getCandidateStatus(candidateId: number): Observable<CandidateStatusResponse> {
        return this.http.get<CandidateStatusResponse>(`${this.coreApiPath}/candidates/${candidateId}/status`);
    }

    removeFromProspecting(candidateId: number): Observable<void> {
        const url = `${this.coreApiPath}/candidate/${candidateId}/remove-from-prospecting`;
        return this.http.post<void>(url, null);
    }

    saveReleaseReason(candidateId: number, releaseReason: CandidateReleaseReasonDto): Observable<void> {
        const url = `${this.coreApiPath}/candidate/${candidateId}/release-reason`;
        return this.http.post<void>(url, releaseReason);
    }

    getReleaseReasons(): Observable<CandidateReleaseReasonsDto[]> {
        const url = `${this.coreApiPath}/candidate/release/reasons`;
        return this.http.get<CandidateReleaseReasonsDto[]>(url);
    }
}
